"use client";
/* eslint-disable @next/next/no-img-element */

import React, { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import styled from "styled-components";
import { InfoContainer, Placeholder, Text } from "@lmn/design-system";
import IconGallery from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconGallery.js";
import IconTimer from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTimer.js";
import { HotelImageEntry } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";
import media from "@/styles/mediaQueries";
import { normalizeMediaItems } from "./utils/imageCategories";
import {
  getOptimizedImageUrl,
  galleryPreview,
} from "@/lib/Helpers/imageOptimization";
import PlayVideo from "@/Icons/PlayVideo";
import useGuestPictures from "@/app/api/useGuestPictures";
import useReviews from "@/app/api/useReviews";
import FloatingReviews from "./components/FloatingReviews";
import { AppConfig } from "@/lib/Types/AppConfig";
import NoImagesPlaceHolder from "@/components/UI/NoImagesPlaceHolder/NoImagesPlaceHolder";
import { useDateListStore } from "@/lib/stores/dateListStore";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";

const FullGalleryDialog = dynamic(
  () => import("./components/FullGalleryDialog"),
  { ssr: false }
);

const GalleryWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  ${media.screenSmMin`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "main main small1 small2"
      "main main small3 small4";
    overflow-x: visible;
  `}
`;

const ImageSlot = styled.div<{ $area: string; $isMain?: boolean }>(
  ({ theme, $area, $isMain }) => ({
    position: "relative",
    flexShrink: 0,
    width: "290px",
    height: "300px",
    scrollSnapAlign: "start",
    overflow: "hidden",
    backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
    gridArea: $area,
    borderRadius: theme.kallisto.border.radius.radius100,

    ...media.screenXsMin({
      width: "340px",
      height: "300px",
    }),

    ...media.screenSmMin({
      width: "100%",
      height: $isMain ? "264px" : "128px",
    }),

    ...media.screenLgMin({
      height: $isMain ? "400px" : "196px",
    }),

    img: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "opacity 0.2s ease, transform 1s ease",
      cursor: "pointer",
      "&:hover": {
        transform: "scale3d(1.1, 1.1, 1.1)",
      },
    },

    ".img-placeholder": {
      position: "absolute",
      inset: 0,
    },
  })
);

const ImageOverlay = styled.div(({ theme }) => ({
  background: theme.kallisto.color.common.black[500],
  zIndex: 0,
  pointerEvents: "none",
  color: theme.kallisto.color.text.static.textOnDark,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  left: 0,
  width: "100%",
  top: 0,
  height: "100%",
  borderRadius: theme.kallisto.border.radius.radius100,

  p: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    margin: 0,
  },

  span: {
    display: "block",
    fontSize: "14px",
    lineHeight: "20px",
  },

  svg: {
    fontSize: "24px",
  },
}));

const PlayButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-width: 0;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.4s ease;
  z-index: 1;
  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.common.black[500]};
    transition: background-color 0.4s ease;
  }

  svg {
    ${media.screenSmMin`
    height: 40px;
    width: 40px;
  `};
    ${media.screenLgMin`
  height: 72px;
  width: 72px;
`};
  }

  svg circle {
    transition: opacity 0.4s ease;
  }

  &:hover svg circle {
    opacity: 0.8;
    transition: opacity 0.4s ease;
  }
`;

const DesktopHighDemandBanner = styled(InfoContainer)(({ theme }) => ({
  position: "absolute",
  bottom: theme.kallisto.space.space100,
  left: theme.kallisto.space.space200,
  right: theme.kallisto.space.space200,
  padding: theme.kallisto.space.space100,
  zIndex: 1,
  pointerEvents: "none",
  display: "none",
  ...media.screenMdMin({
    display: "flex",
  }),
  span: {
    fontWeight: 700,
  },
}));

const MobileHighDemandBanner = styled(InfoContainer)(() => ({
  marginTop: "0.5rem",
  ...media.screenMdMin({
    display: "none",
  }),
}));

const GRID_AREAS = ["main", "small1", "small2", "small3", "small4"];

export default function ImageGalleryClient({
  hotelImageEntries,
  hotelName,
  hotelVideoUrl,
  hotelCID,
  appConfig,
}: {
  hotelImageEntries: HotelImageEntry[];
  hotelName: string;
  hotelVideoUrl: string | null;
  hotelCID: string;
  appConfig: AppConfig;
}) {
  const [loadedMap, setLoadedMap] = useState<Record<string, boolean>>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openAtVideoTab, setOpenAtVideoTab] = useState(false);
  const { data: guestPictures } = useGuestPictures({ hotelCID, appConfig });
  const { data: reviewsData } = useReviews({ cid: hotelCID, appConfig });
  const searchParams = useReactiveSearchParams();
  const searchId = searchParams.get("searchId") ?? "";
  const hotelAlreadyBooked = useDateListStore(
    (state) => state.hotelAlreadyBooked
  );
  // Normalize all media items for the full gallery dialog (images only)
  const allMediaItems = useMemo(
    () => normalizeMediaItems(hotelImageEntries, guestPictures),
    [hotelImageEntries, guestPictures]
  );

  const handleImageLoad = (url: string) => {
    setLoadedMap((prev) => ({ ...prev, [url]: true }));
  };

  const handleImageClick = (index: number) => {
    if (index === 1 && hotelVideoUrl) {
      setOpenAtVideoTab(true);
    }
    setIsDialogOpen(true);
    sendKibanaLog({ envConfig: appConfig.envConfig, message: "Image Gallery Opened", category: "interaction", searchId });
  };

  // Calculate total image count for the overlay
  const totalImageCount =
    hotelImageEntries.length +
    (guestPictures?.guestPictures?.length || 0) +
    (hotelVideoUrl ? 1 : 0);

  const reviewSummary = reviewsData?.reviewSummary;

  return (
    <>
      <GalleryWrapper>
        {reviewSummary && (
          <FloatingReviews
            ratingAverage={reviewSummary.ratingAverage}
            reviewsCount={reviewSummary.reviewsCount}
            recommendationRate={reviewSummary.recommendationRate}
            ratingAverageCategories={reviewSummary.ratingAverageCategories}
            envConfig={appConfig.envConfig}
            searchId={searchId}
          />
        )}
        <GalleryContainer>
          {hotelImageEntries.length === 0 && (
            <NoImagesPlaceHolder hasMinHeight={true} />
          )}
          {hotelImageEntries.length > 0 && GRID_AREAS.map((area, index) => {
            const imageEntry = hotelImageEntries[index];
            const isMain = index === 0;
            const url = imageEntry?.image.url;

            return (
              <ImageSlot
                key={area}
                $area={area}
                $isMain={isMain}
                data-testid={`gallery-image-${index}`}
                onClick={() => handleImageClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleImageClick(index);
                  }
                }}
              >
                {url ? (
                  <>
                    <img
                      src={getOptimizedImageUrl(
                        url,
                        isMain ? 476 : 288,
                        isMain ? 400 : 196
                      )}
                      srcSet={galleryPreview.getSrcSet(url, isMain)}
                      sizes={galleryPreview.getSizes(isMain)}
                      alt={`Hotel image ${index + 1}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      onLoad={() => handleImageLoad(url)}
                      onError={() => handleImageLoad(url)}
                      ref={(img) => {
                        if (img?.complete && !loadedMap[url]) {
                          handleImageLoad(url);
                        }
                      }}
                      style={{ opacity: index === 0 || loadedMap[url] ? 1 : 0 }}
                    />
                    {!loadedMap[url] && (
                      <div className="img-placeholder">
                        <Placeholder
                          width="100%"
                          height="100%"
                          borderRadius="radiusNone"
                        />
                      </div>
                    )}
                    {index === 0 && hotelAlreadyBooked && (
                      <DesktopHighDemandBanner
                        type="danger"
                        icon={IconTimer}
                        message={
                          <Text variant="BodySmall">
                            <Text
                              variant="BodySmall"
                              weight="bold"
                              forceTag="span"
                            >
                              Hohe Nachfrage!
                            </Text>{" "}
                            Dieses Hotel wird von unseren Kunden aktuell häufig
                            gebucht.
                          </Text>
                        }
                      />
                    )}
                    {index === 1 && hotelVideoUrl && (
                      <PlayButton aria-label="Video abspielen">
                        <PlayVideo aria-hidden="true" />
                      </PlayButton>
                    )}
                    {index === 4 && (
                      <ImageOverlay>
                        <IconGallery />
                        <p>Alle Fotos anzeigen</p>
                        <span>({totalImageCount})</span>
                      </ImageOverlay>
                    )}
                  </>
                ) : (
                  <Placeholder width="100%" height="100%" borderRadius="radiusNone" />
                )}
              </ImageSlot>
            );
          })}
        </GalleryContainer>
      </GalleryWrapper>
      {hotelAlreadyBooked && (
        <MobileHighDemandBanner
          type="danger"
          icon={IconTimer}
          message={
            <Text variant="BodySmall">
              <Text variant="BodySmall" weight="bold" forceTag="span">
                Hohe Nachfrage!
              </Text>{" "}
              Dieses Hotel wird von unseren Kunden aktuell häufig gebucht.
            </Text>
          }
        />
      )}

      {isDialogOpen && (
        <FullGalleryDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setOpenAtVideoTab(false);
          }}
          allItems={allMediaItems}
          hotelVideoUrl={hotelVideoUrl}
          hotelName={hotelName}
          openAtVideoTab={openAtVideoTab}
          envConfig={appConfig.envConfig}
          searchId={searchId}
        />
      )}
    </>
  );
}
